import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { FullPageSpinner } from '@/components/FullPageSpinner';
import { useSession } from '@/lib/auth-client';
import { HOME_PATH_BY_ROLE, type Role } from '@shared/schemas/user';

/**
 * Keep the wrong portal off the screen.
 *
 * **This is not access control.** Every endpoint behind these screens is
 * guarded server-side by `requireRole` in src/server/middleware/auth.ts, and
 * that is what actually refuses a request — CLAUDE.md §4: hiding a nav link is
 * not authorization. What this does is stop a client landing on an admin layout
 * that then fails every call inside it, which is a confusing way to be told no.
 *
 * Someone signed in with the wrong role goes to their own home rather than to
 * an error. They are not doing anything wrong; they followed a link that is not
 * theirs.
 */
export function RequireRole({ allow, children }: { allow: readonly Role[]; children: ReactNode }) {
  const location = useLocation();
  const { data: session, isPending } = useSession();

  if (isPending) return <FullPageSpinner />;

  if (!session) {
    // Remember where they were headed so signing in can finish the journey
    // rather than dumping them on a dashboard.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  // No cast needed — inferAdditionalFields in lib/auth-client.ts gives this a
  // real type, so a role that is not one of the three fails to compile.
  const role = session.user.role;
  if (!allow.includes(role)) {
    return <Navigate to={HOME_PATH_BY_ROLE[role]} replace />;
  }

  return <>{children}</>;
}

/** Signed in, any role. For pages every user has, like changing a password. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data: session, isPending } = useSession();

  if (isPending) return <FullPageSpinner />;
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/**
 * The reverse: keep someone who is already signed in off the login page.
 *
 * Without this, following a bookmarked `/login` while signed in shows a form
 * that appears to work and then bounces, which reads as the app being broken.
 */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { data: session, isPending } = useSession();

  if (isPending) return <FullPageSpinner />;
  if (session) return <Navigate to="/launch" replace />;

  return <>{children}</>;
}
